import React, { useState } from 'react';
import AnalysisTable, { AnalysisTableColumn } from './AnalysisTable';
import LoadingSpinner from '../../../utils/LoadingSpinner';
import { downloadCsv } from '../../../utils/analytics/csvExport';
import { useAnalysisEntitlement } from '../../../hooks/analysis/useAnalysisEntitlement';
import './ChartPanel.css';

interface ChartPanelProps {
  title: string;
  subtitle?: string;
  loading?: boolean;
  error?: string | null;
  /** True when there is nothing to plot for the selected range. */
  empty?: boolean;
  emptyMessage?: string;
  footnote?: string;
  /** When provided, a "Table" toggle renders the same data as a table
   *  (the accessibility/relief channel for low-contrast series colors). */
  table?: {
    columns: AnalysisTableColumn[];
    rows: Array<Record<string, any>>;
  };
  /** When provided (and the tier is Enterprise), an Export CSV button appears. */
  csv?: {
    filename: string;
    columns: AnalysisTableColumn[];
    rows: Array<Record<string, any>>;
  };
  children: React.ReactNode;
}

const ChartPanel: React.FC<ChartPanelProps> = ({
  title,
  subtitle,
  loading = false,
  error = null,
  empty = false,
  emptyMessage = 'No data for this range.',
  footnote,
  table,
  csv,
  children,
}) => {
  const [showTable, setShowTable] = useState(false);
  const { entitlement } = useAnalysisEntitlement();
  const canExport = csv && entitlement.tier === 'enterprise';

  let body: React.ReactNode;
  if (loading) {
    body = <div className="chart-panel-state"><LoadingSpinner /></div>;
  } else if (error) {
    body = <div className="chart-panel-state chart-panel-error">⚠️ {error}</div>;
  } else if (empty) {
    body = <div className="chart-panel-state">{emptyMessage}</div>;
  } else if (showTable && table) {
    body = <AnalysisTable columns={table.columns} rows={table.rows} maxRows={30} />;
  } else {
    body = children;
  }

  return (
    <section className="chart-panel">
      <div className="chart-panel-header">
        <div>
          <h3 className="chart-panel-title">{title}</h3>
          {subtitle && <p className="chart-panel-subtitle">{subtitle}</p>}
        </div>
        <div className="chart-panel-actions">
          {canExport && !loading && !error && !empty && (
            <button
              type="button"
              className="chart-panel-toggle"
              onClick={() => downloadCsv(csv!.filename, csv!.columns, csv!.rows)}
            >
              Export CSV
            </button>
          )}
          {table && !loading && !error && !empty && (
            <button
              type="button"
              className="chart-panel-toggle"
              onClick={() => setShowTable(t => !t)}
            >
              {showTable ? 'Chart' : 'Table'}
            </button>
          )}
        </div>
      </div>
      <div className="chart-panel-body">{body}</div>
      {footnote && <p className="chart-panel-footnote">{footnote}</p>}
    </section>
  );
};

export default ChartPanel;
